/**
 * AssignedTasksListPage — filtered list view for a specific "bucket":
 *   ?view=follow_up  → tasks in progress / awaiting closure
 *   ?view=ready      → tasks marked closed (awaiting admin's final audit)
 *   ?view=closed     → finalised tasks (read-only)
 *   ?view=all        → everything
 *
 * Reached from any of the 4 tracking buttons in AssignedTasksHub.
 * Row click → task detail page (/dashboard/cloudgm/assigned-tasks/:id).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Search, User, Store, Trash2,
  Clock, AlertCircle, CheckCircle2, XCircle, ChevronRight, ChevronLeft,
  Lock, ClipboardCheck, PlayCircle, ListTodo,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:     { ar: 'قيد الانتظار', en: 'Pending',     color: '#f59e0b', Icon: Clock },
  in_progress: { ar: 'قيد التنفيذ',  en: 'In progress', color: '#3b82f6', Icon: AlertCircle },
  completed:   { ar: 'مكتملة',       en: 'Completed',   color: '#10b981', Icon: CheckCircle2 },
  cancelled:   { ar: 'ملغاة',        en: 'Cancelled',   color: '#ef4444', Icon: XCircle },
};

const PRIORITY_META = {
  low:    { ar: 'منخفضة', en: 'Low',     color: '#71717a' },
  medium: { ar: 'متوسطة', en: 'Medium',  color: '#f59e0b' },
  high:   { ar: 'عالية',  en: 'High',    color: '#ef4444' },
  urgent: { ar: 'ممتاز',  en: 'Urgent',  color: '#d4af37' },
};

const VIEW_META = {
  // iter-197 (Feb 2026) — Filter tabs now MIRROR the 4 in-task statuses
  // shown on the StatusHero card (Active / In-progress / Completed / Closed).
  active:      { ar: 'فعال',                        en: 'Active',        color: '#0ea5e9', Icon: PlayCircle,      empty_ar: 'لا توجد طلبات فعّالة حالياً.',           empty_en: 'No active requests right now.' },
  in_progress: { ar: 'قيد التنفيذ',                 en: 'In progress',   color: '#d4af37', Icon: PlayCircle,      empty_ar: 'لا توجد طلبات قيد التنفيذ حالياً.',      empty_en: 'No in-progress requests.' },
  completed:   { ar: 'تم الانتهاء',                 en: 'Completed',     color: '#10b981', Icon: ClipboardCheck,  empty_ar: 'لا توجد طلبات تم إنجازها ولم تُغلق بعد.', empty_en: 'No completed-not-closed requests.' },
  closed:      { ar: 'إغلاق الطلب',                 en: 'Closed',        color: '#71717a', Icon: Lock,            empty_ar: 'لا توجد طلبات مغلقة نهائياً بعد.',        empty_en: 'No finalised requests yet.' },
  all:         { ar: 'كل الطلبات',                  en: 'All requests',  color: '#8b5cf6', Icon: ListTodo,        empty_ar: 'لا توجد طلبات. أضف أول طلب من الصفحة الرئيسية.', empty_en: 'No requests yet. Add one from the hub.' },
  // Legacy aliases kept so old bookmarks still resolve.
  follow_up:   { ar: 'متابعة المهام',               en: 'Follow-up',     color: '#f59e0b', Icon: PlayCircle,      empty_ar: 'لا مهام قيد المتابعة حالياً.',           empty_en: 'No tasks under follow-up.' },
  ready:       { ar: 'المهام المنجزة والجاهزة للتدقيق', en: 'Done — awaiting audit', color: '#d4af37', Icon: ClipboardCheck, empty_ar: 'لا مهام جاهزة للتدقيق حالياً.', empty_en: 'No tasks awaiting audit.' },
};

// iter-217c-v13 — small local debounce hook (no external dep). Debounces
// the search box so keystrokes don't spam the network; also ensures the
// cache key is stable while the user is typing.
function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}


export default function AssignedTasksListPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  // iter-246 (Feb 2026) — Only admin can see/press the row-delete button.
  const isAdminRole = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const view = ['active', 'in_progress', 'completed', 'closed', 'all', 'follow_up', 'ready'].includes(urlParams.get('view')) ? urlParams.get('view') : 'active';
  const meta = VIEW_META[view];
  const Arrow = language === 'ar' ? ChevronLeft : ChevronRight;
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [filterType, setFilterType] = useState('all');

  // iter-217c-v13 (Feb 2026) — stale-while-revalidate cache keyed by
  // `view|target_type|q`. Switching between the top tabs was rebuilding the
  // whole list from scratch on every click (setLoading(true) → blank → wait
  // → render). Now: (a) if we've fetched this key before, we render the
  // cached rows INSTANTLY and only silently refresh in the background — no
  // blank state, no spinner flash. (b) The initial load for a key still
  // shows the spinner. (c) The debounced search prevents keystrokes from
  // spamming the network. Response is already <150 KB per iter-217c-v11, so
  // background refresh is virtually free.
  const cacheRef = useRef(new Map()); // key → { data, ts }
  const debouncedQ = useDebounced(searchQ, 350);
  const cacheKey = `${view}|${filterType}|${debouncedQ.trim()}`;

  const load = useCallback(async ({ silent = false } = {}) => {
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      // Instant paint from cache
      setTasks(cached.data);
      setLoading(false);
      // Refresh in background if data is >30 s old
      if (Date.now() - cached.ts < 30_000) return;
    } else if (!silent) {
      setLoading(true);
    }
    try {
      const params = { view };
      if (filterType !== 'all') params.target_type = filterType;
      if (debouncedQ.trim())    params.q = debouncedQ.trim();
      const r = await axios.get(`${API_URL}/api/admin/assigned-tasks`, {
        headers: getAuthHeaders(), params,
      });
      const list = r.data.data || [];
      cacheRef.current.set(cacheKey, { data: list, ts: Date.now() });
      setTasks(list);
    } catch {
      if (!cached) toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [view, filterType, debouncedQ, cacheKey, isAr]);

  useEffect(() => { load(); }, [load]);

  const removeTask = async (id) => {
    if (!window.confirm(t('حذف هذه المهمة؟', 'Delete this task?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/assigned-tasks/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const Icon = meta.Icon;

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="assigned-tasks-list-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${meta.color}10` }} />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/cloudgm/assigned-tasks" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'إدراج مهام', label_en: 'Manual Tasks', path: '/dashboard/cloudgm/assigned-tasks' },
            { label_ar: meta.ar, label_en: meta.en, path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: `${meta.color}15`, border: `1px solid ${meta.color}55` }}>
            <Icon className="w-7 h-7" style={{ color: meta.color }} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
              {isAr ? meta.ar : meta.en}
            </h1>
            <p className="text-white/50 text-sm mt-0.5">
              {view === 'follow_up' && t('المهام النشطة قيد التنفيذ', 'Active tasks currently being worked on')}
              {view === 'ready'     && t('مهام أنهى الموظف كل خطواتها وتنتظر التدقيق النهائي', 'Executor finished all steps, awaiting final admin audit')}
              {view === 'closed'    && t('مهام تم إغلاقها والتدقيق عليها — للقراءة فقط', 'Finalised & audited tasks — read-only')}
              {view === 'all'       && t('كامل سجل المهام', 'Full task log')}
            </p>
          </div>
          <div className="text-4xl font-bold" style={{ color: meta.color }} data-testid="list-total">
            {tasks.length}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-white/50 text-xs mb-1.5">{t('البحث', 'Search')}</label>
              <div className="relative">
                <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
                <Input value={searchQ}
                       onChange={(e) => setSearchQ(e.target.value)}
                       placeholder={t('اسم / هاتف / رقم طلب / خدمة', 'Name / phone / request # / service')}
                       className="bg-[#050505] border-white/10 text-white ps-9"
                       data-testid="list-search" />
              </div>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5">{t('نوع الهدف', 'Target type')}</label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                      dir={isAr ? 'rtl' : 'ltr'}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm"
                      data-testid="list-filter-type">
                <option value="all">{t('الكل', 'All')}</option>
                <option value="customer">{t('عملاء', 'Customers')}</option>
                <option value="agent">{t('وكلاء', 'Agents')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={load} variant="outline"
                      className="w-full border-white/10 bg-transparent text-white/80 hover:bg-white/5">
                <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
                {t('تحديث', 'Refresh')}
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="p-16 text-center">
              <Icon className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: meta.color }} />
              <p className="text-white/50 text-sm">{isAr ? meta.empty_ar : meta.empty_en}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {tasks.map(task => (
                <TaskRow key={task.id}
                         task={task}
                         onOpen={() => navigate(`/dashboard/cloudgm/assigned-tasks/${task.id}`)}
                         onDelete={isAdminRole ? () => removeTask(task.id) : null}
                         t={t} isAr={isAr} Arrow={Arrow} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


function TaskRow({ task, onOpen, onDelete, t, isAr, Arrow }) {
  const status   = STATUS_META[task.status]   || STATUS_META.pending;
  const priority = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const isCustomer = task.target_type === 'customer';
  const accent = isCustomer ? '#3b82f6' : '#8b5cf6';
  const service = isAr ? task.service_name_ar : (task.service_name_en || task.service_name_ar);
  // iter-223 — show the sub-service name on the outer card as well, so
  // operators can distinguish "License / Trade License Renewal" from
  // "License / New License Application" without having to open each row.
  const subService = isAr
    ? (task.sub_service_name_ar || '')
    : (task.sub_service_name_en || task.sub_service_name_ar || '');
  const StatusIcon = status.Icon || Clock;
  const isFinalised = task.review_status === 'closed';

  return (
    <div
      className="p-4 hover:bg-white/[0.03] transition-colors cursor-pointer"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      data-testid={`assigned-task-row-${task.id}`}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}>
          {isCustomer ? <User className="w-5 h-5 text-blue-400" /> : <Store className="w-5 h-5 text-purple-400" />}
        </div>

        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {task.request_no_display && (
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-md border"
                    style={{ backgroundColor: `${accent}20`, borderColor: `${accent}40`, color: accent }}>
                {task.request_no_display}
              </span>
            )}
            <h3 className="text-white font-semibold text-sm">
              {service}
            </h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: `${priority.color}20`, color: priority.color }}>
              {isAr ? priority.ar : priority.en}
            </span>
            {task.review_status === 'ready_for_review' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40 inline-flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" />
                {t('جاهزة للتدقيق', 'Ready for audit')}
              </span>
            )}
            {isFinalised && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t('مغلقة', 'Locked')}
              </span>
            )}
          </div>
          {subService && (
            <div className="text-[11px] text-cyan-300/80 mb-1 inline-flex items-center gap-1"
                 data-testid={`assigned-task-sub-service-${task.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/60" />
              {t('الخدمة الفرعية', 'Sub-service')}: <span className="text-white/90">{subService}</span>
            </div>
          )}
          <div className="text-white/60 text-xs mb-1">
            {isCustomer ? t('المستهدف', 'Target') : t('الوكيل', 'Agent')}: <span className="text-white">{task.target_name}</span>
            {task.target_phone && <span className="ms-2 font-mono text-white/40" dir="ltr">{task.target_phone}</span>}
            {task.target_email && <span className="ms-2 text-white/40" dir="ltr">{task.target_email}</span>}
          </div>
          {task.assigned_to_name && (
            <div className="text-white/50 text-xs">
              {t('منفذ المهمة', 'Executor')}: <span className="text-white/80">{task.assigned_to_name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-md border flex items-center gap-1"
                style={{ color: status.color, borderColor: `${status.color}55`, backgroundColor: `${status.color}15` }}
                data-testid={`assigned-task-status-pill-${task.id}`}>
            <StatusIcon className="w-3 h-3" />
            {isAr ? status.ar : status.en}
          </span>
          {!isFinalised && onDelete && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                    data-testid={`assigned-task-delete-${task.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <Arrow className="w-4 h-4 text-white/30" />
        </div>
      </div>
    </div>
  );
}
